import { useMemo } from "react";
import { StockOrder, StockTrade } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";

interface Props {
  orders: StockOrder[];
  trades: StockTrade[];
  currentPrice: number;
}

export default function OrderBook({ orders, trades, currentPrice }: Props) {
  const { isDark } = useTheme();

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const rowHover = isDark ? "hover:bg-white/3" : "hover:bg-gray-50";
  const divider = isDark ? "border-white/5" : "border-gray-100";

  const openOrders = orders.filter((o) => o.status === "open");

  const buyOrders = useMemo(() =>
    openOrders
      .filter((o) => o.type === "buy" && o.limitPrice)
      .sort((a, b) => (b.limitPrice ?? 0) - (a.limitPrice ?? 0))
      .slice(0, 8),
    [openOrders]
  );

  const sellOrders = useMemo(() =>
    openOrders
      .filter((o) => o.type === "sell" && o.limitPrice)
      .sort((a, b) => (a.limitPrice ?? 0) - (b.limitPrice ?? 0))
      .slice(0, 8),
    [openOrders]
  );

  const recentTrades = useMemo(() =>
    [...trades].sort((a, b) => new Date(b.tradedAt).getTime() - new Date(a.tradedAt).getTime()).slice(0, 10),
    [trades]
  );

  const maxBuyVol = Math.max(...buyOrders.map((o) => o.shares), 1);
  const maxSellVol = Math.max(...sellOrders.map((o) => o.shares), 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Order Book */}
      <div className={`rounded-2xl border ${cardBg}`}>
        <div className={`px-4 py-3 border-b ${divider}`}>
          <h3 className={`text-sm font-bold ${textPrimary}`}>注文板</h3>
        </div>
        <div className="p-3">
          {/* Header */}
          <div className={`grid grid-cols-3 text-xs font-semibold mb-2 px-2 ${textSecondary}`}>
            <span>価格</span>
            <span className="text-center">数量</span>
            <span className="text-right">注文者</span>
          </div>

          {/* Sell Orders (上から安い順) */}
          <div className="space-y-0.5 mb-2">
            {sellOrders.length === 0 ? (
              <p className={`text-xs text-center py-2 ${textSecondary}`}>売り注文なし</p>
            ) : (
              [...sellOrders].reverse().map((order) => (
                <div key={order.id} className={`relative grid grid-cols-3 text-xs px-2 py-1.5 rounded-lg transition-colors ${rowHover}`}>
                  <div
                    className="absolute inset-y-0 right-0 rounded-lg bg-red-400/10"
                    style={{ width: `${(order.shares / maxSellVol) * 60}%` }}
                  ></div>
                  <span className="relative text-red-400 font-bold">{(order.limitPrice ?? 0).toLocaleString()}</span>
                  <span className={`relative text-center ${textPrimary}`}>{order.shares.toLocaleString()}</span>
                  <span className={`relative text-right truncate ${textSecondary}`}>{order.placedByName}</span>
                </div>
              ))
            )}
          </div>

          {/* Current Price */}
          <div className={`flex items-center justify-center gap-2 py-2 border-y my-1 ${divider}`}>
            <span className="text-amber-400 font-black text-base">{currentPrice.toLocaleString()}</span>
            <span className={`text-xs ${textSecondary}`}>現在値</span>
          </div>

          {/* Buy Orders */}
          <div className="space-y-0.5 mt-2">
            {buyOrders.length === 0 ? (
              <p className={`text-xs text-center py-2 ${textSecondary}`}>買い注文なし</p>
            ) : (
              buyOrders.map((order) => (
                <div key={order.id} className={`relative grid grid-cols-3 text-xs px-2 py-1.5 rounded-lg transition-colors ${rowHover}`}>
                  <div
                    className="absolute inset-y-0 right-0 rounded-lg bg-emerald-400/10"
                    style={{ width: `${(order.shares / maxBuyVol) * 60}%` }}
                  ></div>
                  <span className="relative text-emerald-400 font-bold">{(order.limitPrice ?? 0).toLocaleString()}</span>
                  <span className={`relative text-center ${textPrimary}`}>{order.shares.toLocaleString()}</span>
                  <span className={`relative text-right truncate ${textSecondary}`}>{order.placedByName}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Trades */}
      <div className={`rounded-2xl border ${cardBg}`}>
        <div className={`px-4 py-3 border-b ${divider}`}>
          <h3 className={`text-sm font-bold ${textPrimary}`}>最近の取引</h3>
        </div>
        <div className="p-3">
          <div className={`grid grid-cols-3 text-xs font-semibold mb-2 px-2 ${textSecondary}`}>
            <span>価格</span>
            <span className="text-center">数量</span>
            <span className="text-right">時刻</span>
          </div>
          <div className="space-y-0.5">
            {recentTrades.length === 0 ? (
              <p className={`text-xs text-center py-4 ${textSecondary}`}>取引履歴なし</p>
            ) : (
              recentTrades.map((trade, i) => {
                const prev = recentTrades[i + 1];
                const isUp = !prev || trade.price >= prev.price;
                return (
                  <div key={trade.id} className={`grid grid-cols-3 text-xs px-2 py-1.5 rounded-lg transition-colors ${rowHover}`}>
                    <span className={`font-bold ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                      {trade.price.toLocaleString()}
                    </span>
                    <span className={`text-center ${textPrimary}`}>{trade.shares.toLocaleString()}</span>
                    <span className={`text-right ${textSecondary}`}>
                      {new Date(trade.tradedAt).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
